import type { RouteLocationRaw } from 'vue-router'
import { DEFAULT_LOCALE, defaultLocalePath, isAppLocale, localizedPath, type AppLocale } from '~/constants/appLocale'

/** @nuxtjs/i18n names a localized route "<name>___<locale>"; an unlocalized one has no suffix. */
const LOCALIZED_NAME_RE = /^(.+)___([a-z]+)$/

/**
 * Where a link goes in a given language (the current one by default).
 *
 * A marketing page has a URL per language (/pricing, /cs/cenik, /de/preise,
 * constants/localizedRoutes.ts); the app and the blog have one, which every
 * language shares. So "/pricing" becomes "/cs/cenik" for a Czech reader while
 * "/cv/new" and "/blog/…" stay as they are. The module's own localePath()
 * returns "" for a page with no Czech route and knows nothing of translated
 * slugs, which is why links go through this instead.
 *
 * Accepts what NuxtLink's `to` accepts; external URLs, named routes and
 * anything it cannot resolve come back unchanged.
 */
export function useLocalizedPath() {
  // Not useI18n(): this also runs in route middleware and click handlers.
  const nuxtApp = useNuxtApp()
  const router = useRouter()

  function localizePath(path: string, target: AppLocale): string {
    if (!path.startsWith('/') || path.startsWith('//')) return path
    const english = defaultLocalePath(path)
    const resolved = router.resolve(english)
    const match = typeof resolved.name === 'string' ? LOCALIZED_NAME_RE.exec(resolved.name) : null
    if (!match || resolved.matched.length === 0) return path
    if (!router.hasRoute(`${match[1]}___${target}`)) return path
    return localizedPath(english, target)
  }

  return function localize<T extends RouteLocationRaw | string | null | undefined>(to: T, target?: AppLocale): T {
    const current = nuxtApp.$i18n.locale.value
    const lang = target ?? (isAppLocale(current) ? current : DEFAULT_LOCALE)
    if (typeof to === 'string') return localizePath(to, lang) as T
    if (to && typeof to === 'object' && 'path' in to && typeof to.path === 'string' && !('name' in to && to.name)) {
      return { ...to, path: localizePath(to.path, lang) } as T
    }
    return to
  }
}
